import { types, type Instance } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';

/** The editable change keys of an attribute (everything but `id`). */
type AttributeChangeKey =
  | 'name'
  | 'typeName'
  | 'description'
  | 'implementation'
  | 'validation';

/**
 * Store the changes in an attribute that are currently pending.
 */
const AttributeChanges = types
  .model('AttributeChanges', {
    id: types.string,
    name: types.optional(types.maybeNull(types.string), null),
    typeName: types.optional(types.maybeNull(types.string), null),
    description: types.optional(types.maybeNull(types.string), null),
    implementation: types.optional(types.maybeNull(types.string), null),
    validation: types.optional(types.maybeNull(types.string), null),
  })
  .views(self => ({
    // the attribute is dirty if any of the properties has changes
    get dirty() {
      return (
        self.name !== null ||
        self.typeName !== null ||
        self.description !== null ||
        self.implementation !== null ||
        self.validation !== null
      );
    },
  }))
  .actions(self => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the property and clears
     * the stored change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: AttributeChangeKey, original: string | null, changed: string | null) {
      if (original !== changed) {
        self[key] = changed;
      } else {
        self[key] = null;
      }
    },
  }));

type IAttributeChanges = Instance<typeof AttributeChanges>;

/**
 * Store the changes in an attribute inspector that are currently pending.
 */
const AttributesInspectorChanges = types
  .model('AttributesInspectorChanges', {
    // the attributes that have been removed
    removed: types.array(types.string),
    // the attributes that may have pending changes
    changed: types.array(AttributeChanges),
    // the attributes that are added
    added: types.array(AttributeChanges),
  })
  .views(self => ({
    // we are dirty if there are attributes added or removed or there is an
    // attribute in the potentially changed list that is dirty
    get dirty() {
      return self.removed.length > 0 || self.changed.some(cs => cs.dirty) || self.added.length > 0;
    },
    // is an attribute removed?
    isRemoved(id: string) {
      return self.removed.includes(id);
    },
    // is an attribute dirty?
    isDirty(id: string) {
      return self.changed.some(c => c.id === id && c.dirty);
    },
    // are there pending changes for the property with the given key of the
    // attribute with the given id?
    hasChanged(id: string, key: AttributeChangeKey) {
      return self.changed.some(c => c.id === id && c[key] !== null);
    },
    // is the attribute with the given id new?
    isNew(id: string) {
      return self.added.some(c => c.id === id);
    },
    // return the 'AttributeChanges' store for the attribute with the given id
    changeSet(id: string) {
      return self.changed.find(c => c.id === id) || self.added.find(c => c.id === id);
    },
    // return all 'AttributeChanges' stores
    get changeSets() {
      return self.changed.concat(self.added);
    },
    // collect all type names used
    get usedTypeNames() {
      // `changeSets` is a sibling view in this same block (sibling-view wall);
      // read it through a minimal interface alias (§4). Same node at runtime.
      const selfViews = self as unknown as { changeSets: IAttributeChanges[] };
      return selfViews.changeSets.filter(cs => cs.typeName !== null).map(cs => cs.typeName);
    },
  }))
  .actions(self => ({
    discard() {
      // MST accepts an empty array to clear an array prop at runtime; the write
      // type is the concrete IMSTArray, so cast these resets (documented gap).
      self.removed = [] as unknown as typeof self.removed;
      self.changed = [] as unknown as typeof self.changed;
      self.added = [] as unknown as typeof self.added;
    },
    remove(id: string) {
      if (self.isNew(id)) {
        // `changeSet` returns possibly-undefined; legacy behaviour relies on it
        // being present here (guarded by `isNew`). MST's `array.remove` wants the
        // concrete instance, so assert + cast at this single call.
        self.added.remove(self.changeSet(id) as unknown as IAttributeChanges);
      } else {
        self.removed.push(id);
      }
    },
    undoRemove(id: string) {
      self.removed.remove(id);
    },
    addAttribute() {
      const id = uuid();
      self.added.push({
        id,
        name: '',
        typeName: 'String',
        description: '',
        implementation: '',
        validation: '',
      });
      return id;
    },
    // remove the last added attribute
    undoAddAttribute() {
      // Reassigning a sliced plain array to an MST array prop: MST hydrates the
      // snapshot at runtime, the instance write type doesn't accept it — cast.
      self.added = self.added.slice(0, -1) as unknown as typeof self.added;
    },
    /**
     * Store a change.
     *
     * Forwards to the appropriate 'AttributeChanges' store or adds a new
     * 'AttributeChanges' store to the list of possibly changed attributes.
     *
     * @param {*} id the attribute of the change
     * @param {*} key the changed property
     * @param {*} original the original value of the property
     * @param {*} changed the new value of the property
     */
    storeChange(
      id: string,
      key: AttributeChangeKey,
      original: string | null,
      changed: string | null
    ) {
      const cs = self.changeSet(id);
      if (cs) {
        cs.storeChange(key, original, changed);
      } else if (original !== changed) {
        self.changed.push({
          id,
          [key]: changed,
        });
      }
    },
  }));

/**
 * UI state for an attributes inspector
 */
const AttributesInspector = types
  .model('AttributesInspector', {
    // the pending changes
    changes: types.optional(AttributesInspectorChanges, () =>
      AttributesInspectorChanges.create({
        removed: [],
        changed: [],
        added: [],
      })
    ),
    // the id of the attribute that is currently being edited
    editing: types.optional(types.maybeNull(types.string), null),
    // are we editing a new attribute?
    adding: false,
  })
  .views(self => ({
    get dirty() {
      return self.changes.dirty;
    },
    isEditing(id: string) {
      return self.editing === id;
    },
    // get an array with the ids of the removed attributes
    get removals() {
      return self.changes.removed.slice();
    },
    // get an array with the properties of the attributes that changed
    get edits() {
      return self.changes.changed
        .filter(cs => cs.dirty)
        .map(cs => ({
          id: cs.id,
          name: cs.name,
          typeName: cs.typeName,
          description: cs.description,
          implementation: cs.implementation,
          validation: cs.validation,
        }));
    },
    // get an array with the added attributes
    get additions() {
      return self.changes.added
        .filter(cs => cs.dirty)
        .map(cs => ({
          name: cs.name,
          typeName: cs.typeName,
          description: cs.description,
          implementation: cs.implementation,
          validation: cs.validation,
        }));
    },
  }))
  .actions(self => {
    // `discardChanges` calls the sibling action `discardEditing` defined in this
    // same block; expose it through an interface alias so the forward reference
    // types (§4). Property writes stay on the raw `self`.
    const selfActions = self as unknown as { discardEditing(): void };
    return {
    discardChanges() {
      self.changes.discard();
      selfActions.discardEditing();
    },
    discardEditing() {
      self.editing = null;
      self.adding = false;
    },
    startEdit(id: string, adding = false) {
      self.editing = id;
      self.adding = adding;
    },
    stopEdit() {
      self.editing = null;
      self.adding = false;
    },
    };
  });

export type IAttributesInspector = Instance<typeof AttributesInspector>;

export default AttributesInspector;
